import { useState, useEffect, useCallback } from "react";
import { Observation } from "../components/observations/ObservationsPanel";
import ConstructionCompanyProvider from "../infra/provider/constructionCompanyProvider";
import EnterpriseProvider from "../infra/provider/enterpriseProvider";

interface UseObservationsProps {
  entityId: string;
  entityType: "enterprise" | "incorporadora";
  onObservationsChange?: (observations: Observation[]) => void;
  initialObservations?: Observation[];
}

// Interface para representar a estrutura dos comentários da API
interface ConstructionCompanyComment {
  id: number;
  comment: string;
  createdAt: number; // timestamp
  user: {
    id: number;
    email: string;
    status: string;
    userType: string;
    createdAt: number;
  };
  companyId: number;
  updatedAt?: number; // timestamp opcional
}

// Interface para representar a estrutura dos comentários da API de Enterprise
interface EnterpriseComment {
  id: number;
  comment: string;
  createdAt: number; // timestamp
  user: {
    id: number;
    email: string;
    status: string;
    userType: string;
    createdAt: number;
  };
  enterpriseId: number;
  updatedAt?: number; // timestamp opcional
}

export const useObservations = ({
  entityId,
  entityType,
  onObservationsChange,
  initialObservations = [],
}: UseObservationsProps) => {
  const [observations, setObservations] =
    useState<Observation[]>(initialObservations);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Instâncias dos providers
  const constructionCompanyProvider = ConstructionCompanyProvider.getInstance();
  const enterpriseProvider = EnterpriseProvider.getInstance();

  // Carregar observações do localStorage ou API
  const loadObservations = useCallback(async () => {
    if (!entityId) return;

    setIsLoading(true);
    setError(null);

    try {
      if (entityType === "incorporadora") {
        // Usar a API real para incorporadoras
        const apiObservations =
          await constructionCompanyProvider.getConstructionCompanyComments(
            entityId
          );

        // A API retorna um objeto com a propriedade 'comments'
        const comments = (apiObservations as any)?.comments || [];

        if (Array.isArray(comments) && comments.length > 0) {
          // Converter o formato da API para o formato do componente
          const convertedObservations: Observation[] = comments.map(
            (comment: any) => ({
              id: comment.id.toString(),
              text: comment.comment,
              responsible: comment.user?.email || "Usuário",
              createdAt: new Date(comment.createdAt * 1000).toISOString(), // Converter timestamp para ISO
              updatedAt: comment.updatedAt
                ? new Date(comment.updatedAt * 1000).toISOString()
                : undefined,
            })
          );
          setObservations(convertedObservations);
        } else {
          setObservations([]);
        }
      } else if (entityType === "enterprise") {
        // Usar a API real para enterprises
        const apiObservations = await enterpriseProvider.getEnterpriseComments(
          entityId
        );

        // A API retorna um objeto com a propriedade 'comments'
        const comments = (apiObservations as any)?.comments || [];

        if (Array.isArray(comments) && comments.length > 0) {
          // Converter o formato da API para o formato do componente
          const convertedObservations: Observation[] = comments.map(
            (comment: any) => ({
              id: comment.id.toString(),
              text: comment.comment,
              responsible: comment.user?.email || "Usuário",
              createdAt: new Date(comment.createdAt * 1000).toISOString(), // Converter timestamp para ISO
              updatedAt: comment.updatedAt
                ? new Date(comment.updatedAt * 1000).toISOString()
                : undefined,
            })
          );
          setObservations(convertedObservations);
        } else {
          setObservations([]);
        }
      } else {
        // Para outros tipos, continuar usando localStorage por enquanto
        const storageKey = `observations_${entityType}_${entityId}`;
        const stored = localStorage.getItem(storageKey);

        if (stored) {
          const parsedObservations = JSON.parse(stored);
          setObservations(parsedObservations);
        } else {
          setObservations([]);
        }
      }
    } catch (err) {
      console.error("Erro ao carregar observações:", err);
      setError("Erro ao carregar observações");
    } finally {
      setIsLoading(false);
    }
  }, [entityId, entityType, constructionCompanyProvider, enterpriseProvider]);

  // Salvar observações no localStorage ou API
  const saveObservations = useCallback(
    async (newObservations: Observation[]) => {
      if (!entityId) return;

      try {
        if (entityType === "incorporadora" || entityType === "enterprise") {
          // Para incorporadoras e enterprises, as observações são salvas diretamente via API
          // Não precisamos fazer nada aqui, pois cada operação já salva individualmente
          setObservations(newObservations);
        } else {
          // Para outros tipos, continuar usando localStorage
          const storageKey = `observations_${entityType}_${entityId}`;
          localStorage.setItem(storageKey, JSON.stringify(newObservations));
          setObservations(newObservations);
        }

        if (onObservationsChange) {
          onObservationsChange(newObservations);
        }
      } catch (err) {
        console.error("Erro ao salvar observações:", err);
        setError("Erro ao salvar observações");
      }
    },
    [entityId, entityType, onObservationsChange]
  );

  // Adicionar nova observação
  const addObservation = useCallback(
    async (text: string, responsible: string) => {
      try {
        if (entityType === "incorporadora") {
          // Usar a API real para incorporadoras
          const response =
            await constructionCompanyProvider.createConstructionCompanyComment(
              entityId,
              text
            );

          // Recarregar as observações da API
          await loadObservations();

          return response;
        } else if (entityType === "enterprise") {
          // Usar a API real para enterprises
          const response = await enterpriseProvider.createEnterpriseComment(
            entityId,
            text
          );

          // Recarregar as observações da API
          await loadObservations();

          return response;
        } else {
          // Para outros tipos, continuar usando localStorage
          const newObservation: Observation = {
            id: Date.now().toString(),
            text: text.trim(),
            responsible,
            createdAt: new Date().toISOString(),
          };

          const updatedObservations = [newObservation, ...observations];
          await saveObservations(updatedObservations);

          return newObservation;
        }
      } catch (err) {
        console.error("Erro ao adicionar observação:", err);
        throw err;
      }
    },
    [
      entityId,
      entityType,
      observations,
      saveObservations,
      constructionCompanyProvider,
      enterpriseProvider,
      loadObservations,
    ]
  );

  // Editar observação existente
  const editObservation = useCallback(
    async (id: string, text: string) => {
      try {
        if (entityType === "incorporadora") {
          // Usar a API real para incorporadoras
          await constructionCompanyProvider.updateConstructionCompanyComment(
            id,
            text
          );

          // Recarregar as observações da API
          await loadObservations();
        } else if (entityType === "enterprise") {
          // Usar a API real para enterprises
          await enterpriseProvider.updateEnterpriseComment(id, text);

          // Recarregar as observações da API
          await loadObservations();
        } else {
          // Para outros tipos, continuar usando localStorage
          const updatedObservations = observations.map((obs) =>
            obs.id === id
              ? {
                  ...obs,
                  text: text.trim(),
                  updatedAt: new Date().toISOString(),
                }
              : obs
          );

          await saveObservations(updatedObservations);
        }
      } catch (err) {
        console.error("Erro ao editar observação:", err);
        throw err;
      }
    },
    [
      entityId,
      entityType,
      observations,
      saveObservations,
      constructionCompanyProvider,
      enterpriseProvider,
      loadObservations,
    ]
  );

  // Excluir observação
  const deleteObservation = useCallback(
    async (id: string) => {
      try {
        if (entityType === "incorporadora") {
          // Usar a API real para incorporadoras
          await constructionCompanyProvider.deleteConstructionCompanyComment(
            id
          );

          // Recarregar as observações da API
          await loadObservations();
        } else if (entityType === "enterprise") {
          // Usar a API real para enterprises
          await enterpriseProvider.deleteEnterpriseComment(id);

          // Recarregar as observações da API
          await loadObservations();
        } else {
          // Para outros tipos, continuar usando localStorage
          const updatedObservations = observations.filter(
            (obs) => obs.id !== id
          );
          await saveObservations(updatedObservations);
        }
      } catch (err) {
        console.error("Erro ao excluir observação:", err);
        throw err;
      }
    },
    [
      entityId,
      entityType,
      observations,
      saveObservations,
      constructionCompanyProvider,
      enterpriseProvider,
      loadObservations,
    ]
  );

  // Carregar observações quando entityId mudar
  useEffect(() => {
    loadObservations();
  }, [loadObservations]);

  return {
    observations,
    isLoading,
    error,
    addObservation,
    editObservation,
    deleteObservation,
    reloadObservations: loadObservations,
  };
};
